/**
 * 워크플로 화면의 머리줄 · 단계 리본(2026-10-26 — SQL 튜닝 · SQL 진단 리포트 · SQL 작성 · SQL 품질 검사 · 스키마 비교가 함께 쓴다).
 *
 * 머리줄: 왼쪽은 제목(·부가 정보·「저장 전」 표시), 오른쪽 끝은 화면의 추가 단추 → **삭제 아이콘 → 저장**. 저장은 **지금 단계의 내용**을 저장한다
 * (예전에는 단계마다 맨 아래에 저장 단추가 있어 긴 단계에서는 스크롤해야 보였고, 화면마다 자리가 달랐다 — 사용자 요청으로 SQL 품질 검사 모양으로 맞췄다).
 * 리본: 「1. 대상 · 2. 수집 …」, 이력 같은 단계 밖 탭은 번호 없이 오른쪽 끝.
 * 모양은 Workflow.css 한 곳 — 화면 CSS 에서 stn-head·stn-ribbon 을 다시 칠하지 말 것.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiSave, FiTrash2 } from 'react-icons/fi';
import './Workflow.css';

export default function WorkflowHead({
  title, titleSlot = null, meta = null, badges = null, dirty = false, actions = null,
  onSave = null, saveLabel = null, saveDisabled = false, saveTitle = null, onDelete = null, deleteDisabled = false,
}) {
  const { t } = useTranslation();
  return (
    <div className="stn-head">
      <div className="stn-head-title">
        {titleSlot || <b title={typeof title === 'string' ? title : undefined}>{title}</b>}
        {meta != null && meta !== '' && <span className="stn-note">{meta}</span>}
        {badges}
        {dirty && <span className="stn-warn">{t('common:wf.unsaved')}</span>}
      </div>
      <span className="stn-row stn-head-actions">
        {actions}
        {onDelete && (
          <button type="button" className="app-btn app-btn-danger" onClick={onDelete} disabled={deleteDisabled}
            title={t('common:ui.delete')} aria-label={t('common:ui.delete')}><FiTrash2 size={12} /></button>
        )}
        {onSave && (
          <button type="button" className="app-btn app-btn-primary" onClick={onSave} disabled={saveDisabled}
            title={saveTitle || t('common:wf.saveHint')}><FiSave size={12} /> {saveLabel || t('common:wf.save')}</button>
        )}
      </span>
    </div>
  );
}

WorkflowHead.propTypes = {
  /** 제목 글 — titleSlot 이 있으면 그것을 대신 그린다(이름 바꾸기 입력 칸 등) */
  title: PropTypes.node,
  titleSlot: PropTypes.node,
  meta: PropTypes.node,
  badges: PropTypes.node,
  /** 지금 단계에 저장하지 않은 고침이 있는가 — 「저장 전」 표시 */
  dirty: PropTypes.bool,
  /** 저장·삭제 앞에 놓는 화면의 단추들 */
  actions: PropTypes.node,
  /** 지금 단계 저장 — 없으면(저장할 것이 없는 단계) 단추를 그리지 않는다 */
  onSave: PropTypes.func,
  saveLabel: PropTypes.node,
  saveDisabled: PropTypes.bool,
  saveTitle: PropTypes.string,
  onDelete: PropTypes.func,
  deleteDisabled: PropTypes.bool,
};

/**
 * 단계 리본. tabs 의 각 항목 id 를 label(id, i) 로 그린다. aside 에 든 id(이력 등)는 번호 없이 오른쪽 끝.
 * suffix(id) 는 탭 글 뒤에 붙는 건수·표시(「 (3)」 「 ✓」), disabledOf(id) 가 참이면 누를 수 없다.
 */
export function WorkflowRibbon({ tabs, step, onStep, label, suffix, aside = ['history'], disabledOf, ariaLabel, className = '' }) {
  return (
    <nav className={`stn-ribbon${className ? ` ${className}` : ''}`} role="tablist" aria-label={ariaLabel}>
      {tabs.map((s, i) => {
        const isAside = aside.includes(s);
        return (
          <button key={s} type="button" role="tab" aria-selected={step === s} disabled={disabledOf ? !!disabledOf(s) : false}
            className={`stn-tab${step === s ? ' on' : ''}${isAside ? ' stn-tab-aside' : ''}`} onClick={() => onStep(s)}>
            {isAside ? '' : `${i + 1}. `}{label(s, i)}{suffix ? suffix(s) : ''}
          </button>
        );
      })}
    </nav>
  );
}

WorkflowRibbon.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  step: PropTypes.string,
  onStep: PropTypes.func.isRequired,
  label: PropTypes.func.isRequired,
  suffix: PropTypes.func,
  aside: PropTypes.arrayOf(PropTypes.string),
  disabledOf: PropTypes.func,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

/** 단계 저장 판정 — 그 단계가 고치는 문서 칸이 저장본과 다른가(JSON 비교) */
export function stepDirty(draft, saved, fields) {
  if (!draft || !saved || !fields || !fields.length) return false;
  return fields.some(f => JSON.stringify(draft[f] === undefined ? null : draft[f]) !== JSON.stringify(saved[f] === undefined ? null : saved[f]));
}
